import { useMemo, useState } from 'react';
import { Alert, Badge, Button, Collapse, Modal, Spinner, Table } from 'react-bootstrap';
import { ChevronDown, ChevronRight, ExclamationTriangle } from 'react-bootstrap-icons';
import { computeHygieneIssues, type HygieneIssue } from '@/config/clientConfigFields';
import { clientService } from '@/services/clientService';
import { type ClientConfig } from '@/types';

interface ConfigHygienePanelProps {
  clientName: string;
  config: ClientConfig;
  disabled?: boolean;
  /** Shown when the panel's actions are disabled (e.g. unsaved form edits). */
  disabledNote?: string;
  /** Called with the removed keys so the parent can patch its local state. */
  onCleaned: (removedKeys: string[]) => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

const formatValue = (value: unknown): string => {
  try {
    const s = JSON.stringify(value);
    return s.length > 80 ? `${s.slice(0, 77)}...` : s;
  } catch {
    return String(value);
  }
};

/**
 * FEAT-239 — expandable warning panel listing deprecated and unknown keys
 * present in a client's config record, with one-click removal. Removal uses
 * the targeted REMOVE path (updateClientConfig with undefined values), so it
 * works even for keys the current schema no longer recognises.
 */
export function ConfigHygienePanel({
  clientName,
  config,
  disabled = false,
  disabledNote,
  onCleaned,
  onError,
  onSuccess,
}: ConfigHygienePanelProps) {
  const [open, setOpen] = useState(false);
  const [pendingKeys, setPendingKeys] = useState<string[] | null>(null);
  const [working, setWorking] = useState(false);

  const issues = useMemo(() => computeHygieneIssues(config as Record<string, unknown>), [config]);

  if (issues.length === 0) return null;

  const deprecated = issues.filter((i) => i.kind === 'deprecated');
  const unknown = issues.filter((i) => i.kind === 'unknown');
  // "Remove all deprecated" skips keys with an active caution (e.g. legacy
  // Synergy flags a record still relies on) — those need a deliberate click.
  const safeDeprecatedKeys = deprecated.filter((i) => !i.warning).map((i) => i.key);

  const removeKeys = async (keys: string[]) => {
    try {
      setWorking(true);
      const updates = Object.fromEntries(keys.map((k) => [k, undefined])) as Partial<ClientConfig>;
      await clientService.updateClientConfig(clientName, updates);
      onSuccess(`Removed ${keys.length} key${keys.length !== 1 ? 's' : ''} from ${clientName}: ${keys.join(', ')}`);
      onCleaned(keys);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to remove config keys');
    } finally {
      setWorking(false);
      setPendingKeys(null);
    }
  };

  const renderRows = (list: HygieneIssue[]) =>
    list.map((issue) => (
      <tr key={issue.key}>
        <td>
          <code>{issue.key}</code>
        </td>
        <td>
          <code>{formatValue(issue.value)}</code>
        </td>
        <td className="small">
          {issue.reason}
          {issue.warning && (
            <div className="text-danger mt-1">
              <ExclamationTriangle className="me-1" />
              {issue.warning}
            </div>
          )}
        </td>
        <td className="text-end">
          <Button
            variant="outline-danger"
            size="sm"
            disabled={disabled || working}
            onClick={() => setPendingKeys([issue.key])}
          >
            Remove
          </Button>
        </td>
      </tr>
    ));

  return (
    <>
      <Alert variant="warning" className="mb-3 py-2">
        <div
          role="button"
          className="d-flex align-items-center"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          {open ? <ChevronDown className="me-2" /> : <ChevronRight className="me-2" />}
          <ExclamationTriangle className="me-2" />
          <span className="fw-semibold">Config hygiene</span>
          <Badge bg="warning" text="dark" className="ms-2">
            {issues.length} issue{issues.length !== 1 ? 's' : ''}
          </Badge>
          <span className="text-muted small ms-2">deprecated or unrecognised keys are stored in this config</span>
        </div>
        <Collapse in={open}>
          <div>
            <div className="mt-3">
              {disabled && disabledNote && (
                <Alert variant="secondary" className="py-1 px-2 small mb-2">
                  {disabledNote}
                </Alert>
              )}
              {deprecated.length > 0 && (
                <>
                  <Alert variant="light" className="py-1 px-2 small border mb-2">
                    <ExclamationTriangle className="me-1" />
                    <strong>Before removing:</strong> make sure the image this client runs — or the one you are about to
                    deploy — no longer needs the flag. An older image that still reads a removed flag will fall back to
                    its default on the next deploy.
                  </Alert>
                  <div className="d-flex align-items-center mb-2">
                    <span className="fw-semibold small text-uppercase">Deprecated keys</span>
                    {safeDeprecatedKeys.length > 1 && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="ms-auto"
                        disabled={disabled || working}
                        onClick={() => setPendingKeys(safeDeprecatedKeys)}
                      >
                        Remove all deprecated ({safeDeprecatedKeys.length})
                      </Button>
                    )}
                  </div>
                  <Table size="sm" bordered hover className="bg-white mb-3">
                    <thead>
                      <tr>
                        <th style={{ width: '22%' }}>Key</th>
                        <th style={{ width: '18%' }}>Value</th>
                        <th>Why it should go</th>
                        <th style={{ width: '10%' }} />
                      </tr>
                    </thead>
                    <tbody>{renderRows(deprecated)}</tbody>
                  </Table>
                </>
              )}
              {unknown.length > 0 && (
                <>
                  <div className="fw-semibold small text-uppercase mb-2">Unknown keys</div>
                  <Alert variant="light" className="py-1 px-2 small border mb-2">
                    These may be from a <strong>newer schema version</strong> than this portal build — verify before
                    removing. Removing a key is also the fix when deploying an <strong>older image</strong> whose schema
                    rejects it (<code>unrecognized_keys</code>).
                  </Alert>
                  <Table size="sm" bordered hover className="bg-white mb-2">
                    <thead>
                      <tr>
                        <th style={{ width: '22%' }}>Key</th>
                        <th style={{ width: '18%' }}>Value</th>
                        <th>Note</th>
                        <th style={{ width: '10%' }} />
                      </tr>
                    </thead>
                    <tbody>{renderRows(unknown)}</tbody>
                  </Table>
                </>
              )}
              <div className="text-muted small">
                Removal deletes the key from the stored record only (targeted REMOVE — no full-schema validation, no
                deploy). The deployment picks the change up on the next deploy.
              </div>
            </div>
          </div>
        </Collapse>
      </Alert>

      <Modal show={pendingKeys !== null} onHide={() => !working && setPendingKeys(null)} centered>
        <Modal.Header closeButton={!working}>
          <Modal.Title>Remove config key{(pendingKeys?.length ?? 0) !== 1 ? 's' : ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Remove the following key{(pendingKeys?.length ?? 0) !== 1 ? 's' : ''} from <strong>{clientName}</strong>?
          </p>
          <ul>
            {(pendingKeys ?? []).map((k) => (
              <li key={k}>
                <code>{k}</code>
                {issues.find((i) => i.key === k)?.warning && (
                  <div className="text-danger small">
                    <ExclamationTriangle className="me-1" />
                    {issues.find((i) => i.key === k)?.warning}
                  </div>
                )}
              </li>
            ))}
          </ul>
          <p className="text-muted small mb-0">
            This edits the stored config record only. The change is logged to the activity feed and takes effect on the
            next deploy — confirm the image you plan to deploy no longer reads{' '}
            {(pendingKeys?.length ?? 0) !== 1 ? 'these flags' : 'this flag'} before removing.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPendingKeys(null)} disabled={working}>
            Cancel
          </Button>
          <Button variant="danger" disabled={working} onClick={() => pendingKeys && removeKeys(pendingKeys)}>
            {working ? (
              <>
                <Spinner size="sm" className="me-2" />
                Removing…
              </>
            ) : (
              'Remove'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ConfigHygienePanel;
